import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PostJob.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split('T')[0];
};

const PostJob = () => {
  const navigate = useNavigate();
  const [ships, setShips] = useState([]);
  const [previousVendors, setPreviousVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedShipId, setSelectedShipId] = useState('');
  const [selectedShip, setSelectedShip] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [showCustomPort, setShowCustomPort] = useState(false);
  const [showCustomService, setShowCustomService] = useState(false);
  const [showCustomClass, setShowCustomClass] = useState(false);
  
  const [formData, setFormData] = useState({
    vesselId: '',
    port: '',
    customPort: '',
    serviceType: '',
    customService: '',
    classApproval: '',
    customClassApproval: '',
    description: '',
    budgetMin: '',
    budgetMax: '',
    budgetType: 'fixed',
    currency: 'USD',
    deadline: '',
    targetVendorId: ''
  });
  const [errors, setErrors] = useState({});

  const getToken = () => localStorage.getItem('marilink_token');

  const fetchVessels = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await fetch(`${API_BASE}/vessels`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setShips(data.vessels || []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreviousVendors = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/users/owner/vendors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPreviousVendors(data.vendors || []);
      }
    } catch (error) {
      console.error('Error fetching previous vendors:', error);
    }
  };

  useEffect(() => {
    fetchVessels();
    fetchPreviousVendors();
  }, []);

  const serviceTypes = [
    'GMDSS Annual Survey', 'GMDSS Repair', 'Radio Survey', 'SBM Maintenance', 
    'Battery Replacement', 'Gyro Compass Repair', 'Radar Calibration',
    'AIS Service', 'ECDIS Service', 'EPIRB Testing', 'SART Testing',
    'NAVTEX Service', 'Safety Equipment Inspection', 'Navigation Systems',
    'Technical Spares Supply', 'VSAT & Communication', 'Crane & Winch Service',
    'HVAC & Refrigeration', 'Electrical Engineering', 'Main Engine Overhaul',
    'Hydraulic Systems', 'Ballast Water Systems', 'Tank Cleaning',
    'Lifeboat & Davit Service', 'Fire Fighting Equipment', 'Pest Control',
    'Crew Change Coordination', 'Customs Brokerage', 'Port Agency Services',
    'Ship Chandling', 'Bunkering Coordination', 'Marine Lubricants',
    'Hull Cleaning & Diving', 'Propeller Polishing', 'Underwater Inspection',
    'Medical & Healthcare', 'Lube Oil Analysis', 'Logistics & Storage',
    'Others'
  ];
  
  const ports = [
    'Dubai, UAE', 'Abu Dhabi, UAE', 'Sharjah, UAE', 'Fujairah, UAE', 
    'Jebel Ali, UAE', 'Khor Fakkan, UAE', 'Ras Al Khaimah, UAE',
    'Jeddah, Saudi Arabia', 'Dammam, Saudi Arabia', 'Yanbu, Saudi Arabia',
    'Doha, Qatar', 'Ras Laffan, Qatar', 'Manama, Bahrain', 'Sitra, Bahrain',
    'Muscat, Oman', 'Salalah, Oman', 'Sohar, Oman', 'Duqm, Oman',
    'Kuwait City, Kuwait', 'Shuwaikh, Kuwait',
    'Singapore', 'Hong Kong', 'Shanghai, China', 'Busan, South Korea',
    'Tokyo, Japan', 'Yokohama, Japan', 'Kaohsiung, Taiwan',
    'Jakarta, Indonesia', 'Port Klang, Malaysia', 'Bangkok, Thailand',
    'Ho Chi Minh City, Vietnam', 'Manila, Philippines',
    'Colombo, Sri Lanka', 'Chittagong, Bangladesh',
    'Mumbai, India', 'Chennai, India', 'Kolkata, India',
    'Karachi, Pakistan', 'Gwadar, Pakistan',
    'Lagos, Nigeria', 'Tema, Ghana', 'Abidjan, Ivory Coast',
    'Dakar, Senegal', 'Mombasa, Kenya', 'Dar es Salaam, Tanzania',
    'Durban, South Africa', 'Cape Town, South Africa',
    'Alexandria, Egypt', 'Port Said, Egypt', 'Suez, Egypt',
    'Casablanca, Morocco', 'Tunis, Tunisia', 'Algiers, Algeria',
    'Rotterdam, Netherlands', 'Antwerp, Belgium', 'Hamburg, Germany',
    'London, UK', 'Southampton, UK', 'Liverpool, UK',
    'Marseille, France', 'Barcelona, Spain', 'Valencia, Spain',
    'Genoa, Italy', 'Piraeus, Greece', 'Istanbul, Turkey',
    'Constanta, Romania', 'Odessa, Ukraine',
    'Houston, USA', 'New York, USA', 'Los Angeles, USA',
    'Miami, USA', 'New Orleans, USA', 'Vancouver, Canada',
    'Santos, Brazil', 'Buenos Aires, Argentina',
    'Panama Canal, Panama', 'Kingston, Jamaica',
    'Others'
  ];

  const classApprovals = ['DNV', 'LR', 'ABS', 'BV', 'NK', 'RINA', 'CCS', 'KR', 'IRS', 'Others'];

  const handleShipChange = (e) => {
    const shipId = e.target.value;
    setSelectedShipId(shipId);
    const ship = ships.find(s => s._id === shipId);
    setSelectedShip(ship);
    if (ship) {
      setFormData(prev => ({ ...prev, vesselId: ship._id }));
    }
    if (errors.vesselId) setErrors(prev => ({ ...prev, vesselId: '' }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    
    if (name === 'port') {
      setShowCustomPort(value === 'Others');
      if (value !== 'Others') setFormData(prev => ({ ...prev, customPort: '' }));
    }
    if (name === 'serviceType') {
      setShowCustomService(value === 'Others');
      if (value !== 'Others') setFormData(prev => ({ ...prev, customService: '' }));
    }
    if (name === 'classApproval') {
      setShowCustomClass(value === 'Others');
      if (value !== 'Others') setFormData(prev => ({ ...prev, customClassApproval: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!selectedShipId) newErrors.vesselId = 'Please select a ship';
    if (!formData.port) newErrors.port = 'Vessel port is required';
    if (formData.port === 'Others' && !formData.customPort.trim()) newErrors.port = 'Please specify custom port';
    if (!formData.serviceType) newErrors.serviceType = 'Service type is required';
    if (formData.serviceType === 'Others' && !formData.customService.trim()) newErrors.serviceType = 'Please specify custom service';
    if (formData.classApproval === 'Others' && !formData.customClassApproval.trim()) newErrors.classApproval = 'Please specify class society';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    
    if (formData.budgetType !== 'quotation') {
      if (!formData.budgetMin || parseFloat(formData.budgetMin) <= 0) newErrors.budgetMin = 'Valid minimum amount is required';
      if (!formData.budgetMax || parseFloat(formData.budgetMax) <= 0) newErrors.budgetMax = 'Valid maximum amount is required';
      if (parseFloat(formData.budgetMin) > parseFloat(formData.budgetMax)) {
        newErrors.budgetMax = 'Max must be greater than min';
      }
    }
    
    if (!formData.deadline) newErrors.deadline = 'Deadline is required';
    if (formData.deadline) {
      const selectedDate = new Date(formData.deadline);
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      if (selectedDate < tomorrow) newErrors.deadline = 'Deadline must be tomorrow or later';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const token = getToken();
      const finalServiceType = formData.serviceType === 'Others' ? formData.customService.trim() : formData.serviceType;
      const finalPort = formData.port === 'Others' ? formData.customPort.trim() : formData.port;
      const finalClass = formData.classApproval === 'Others' ? formData.customClassApproval.trim() : formData.classApproval;

      const isPrivate = formData.targetVendorId && formData.targetVendorId !== '';
      const invitedVendorId = isPrivate ? formData.targetVendorId : undefined;

      const payload = {
        vesselId: selectedShipId,
        port: finalPort,
        serviceType: finalServiceType,
        classApproval: finalClass || undefined,
        description: formData.description,
        budgetMin: formData.budgetType === 'quotation' ? undefined : parseFloat(formData.budgetMin),
        budgetMax: formData.budgetType === 'quotation' ? undefined : parseFloat(formData.budgetMax),
        currency: formData.currency,
        budgetType: formData.budgetType,
        deadline: formData.deadline,
        isPrivate: isPrivate,
        invitedVendorId: invitedVendorId
      };

      const response = await fetch(`${API_BASE}/rfqs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      if (data.success) {
        navigate('/owner/my-jobs-quotations');
      } else {
        alert(data.message || 'Failed to post job');
      }
    } catch (err) {
      alert('Error posting job.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="loader-container">
      <i className="fa-solid fa-circle-notch fa-spin"></i>
      <span>Loading vessels...</span>
    </div>
  );

  return (
    <div className="pj-wrapper">
      <div className="pj-header">
        <h1><i className="fa-solid fa-plus"></i> Post a Job</h1>
        <p>Create a new service request and receive competitive quotations</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="pj-section">
          <h2 className="pj-section-title"><i className="fa-solid fa-ship"></i> Select Vessel</h2>
          
          {ships.length === 0 ? (
            <div className="pj-empty-state">
              <i className="fa-solid fa-ship"></i>
              <p>No vessels found in your fleet.</p>
              <button type="button" className="pj-btn pj-btn-primary" onClick={() => navigate('/owner/my-ships')} style={{ margin: '0 auto' }}>
                Add Vessel First
              </button>
            </div>
          ) : (
            <div className="pj-input-group">
              <label className="pj-label">Select from your fleet</label>
              <select className="pj-input" value={selectedShipId} onChange={handleShipChange}>
                <option value="">-- Choose Vessel --</option>
                {ships.map(ship => (
                  <option key={ship._id} value={ship._id}>{ship.name} (IMO: {ship.imo})</option>
                ))}
              </select>
              {errors.vesselId && <span className="pj-error-text">{errors.vesselId}</span>}
            </div>
          )}

          {selectedShip && (
            <div className="pj-info-grid" style={{ marginTop: '16px' }}>
              <div className="pj-info-item"><span className="pj-info-label">Ship Name</span><span className="pj-info-value">{selectedShip.name}</span></div>
              <div className="pj-info-item"><span className="pj-info-label">IMO Number</span><span className="pj-info-value">{selectedShip.imo}</span></div>
              <div className="pj-info-item"><span className="pj-info-label">Ship Type</span><span className="pj-info-value">{selectedShip.type}</span></div>
              <div className="pj-info-item"><span className="pj-info-label">Flag</span><span className="pj-info-value">{selectedShip.flag}</span></div>
              <div className="pj-info-item"><span className="pj-info-label">GRT</span><span className="pj-info-value">{selectedShip.grt ? Number(selectedShip.grt).toLocaleString() : 'N/A'}</span></div>
              <div className="pj-info-item"><span className="pj-info-label">Year Built</span><span className="pj-info-value">{selectedShip.built || 'N/A'}</span></div>
            </div>
          )}
        </div>

        {selectedShip && (
          <div className="pj-section">
            <h2 className="pj-section-title"><i className="fa-solid fa-list-check"></i> Job Requirements</h2>
            
            <div className="pj-grid">
              {/* Service Type dropdown */}
              <div className="pj-input-group">
                <label className="pj-label">Service Type</label>
                <select className="pj-input" name="serviceType" value={formData.serviceType} onChange={handleChange}>
                  <option value="">Select service type</option>
                  {serviceTypes.map(service => <option key={service} value={service}>{service}</option>)}
                </select>
                {errors.serviceType && <span className="pj-error-text">{errors.serviceType}</span>}
              </div>

              {/* Custom service input appears right after service type dropdown */}
              {showCustomService && (
                <div className="pj-input-group">
                  <label className="pj-label">Specify Service Type</label>
                  <input className="pj-input" type="text" name="customService" value={formData.customService} onChange={handleChange} placeholder="Enter custom service..." />
                </div>
              )}

              {/* Port dropdown */}
              <div className="pj-input-group">
                <label className="pj-label">Vessel Port</label>
                <select className="pj-input" name="port" value={formData.port} onChange={handleChange}>
                  <option value="">Select current port</option>
                  {ports.map(port => <option key={port} value={port}>{port}</option>)}
                </select>
                {errors.port && <span className="pj-error-text">{errors.port}</span>}
              </div>

              {/* Custom port input appears right after port dropdown */}
              {showCustomPort && (
                <div className="pj-input-group">
                  <label className="pj-label">Specify Port</label>
                  <input className="pj-input" type="text" name="customPort" value={formData.customPort} onChange={handleChange} placeholder="Enter port, city, country..." />
                </div>
              )}

              {/* Class Approval dropdown */}
              <div className="pj-input-group">
                <label className="pj-label">Class Approval (Optional)</label>
                <select className="pj-input" name="classApproval" value={formData.classApproval} onChange={handleChange}>
                  <option value="">No specific class required</option>
                  {classApprovals.map(cls => <option key={cls} value={cls}>{cls}</option>)}
                </select>
              </div>

              {/* Custom class input appears right after class dropdown */}
              {showCustomClass && (
                <div className="pj-input-group">
                  <label className="pj-label">Specify Class Society</label>
                  <input className="pj-input" type="text" name="customClassApproval" value={formData.customClassApproval} onChange={handleChange} placeholder="Enter class society..." />
                </div>
              )}

              {/* Target Service Provider - Clean without extra text */}
              {previousVendors.length > 0 && (
                <div className="pj-input-group">
                  <label className="pj-label">Target Service Provider (Optional)</label>
                  <select 
                    className="pj-input" 
                    name="targetVendorId" 
                    value={formData.targetVendorId} 
                    onChange={handleChange}
                  >
                    <option value="">-- All Service Providers (Public) --</option>
                    {previousVendors.map(vendor => (
                      <option key={vendor._id} value={vendor._id}>
                        {vendor.company || vendor.name} {vendor.rating ? `(★ ${vendor.rating})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="pj-grid pj-grid-full" style={{ marginTop: '20px' }}>
              <div className="pj-input-group">
                <label className="pj-label">Scope of Work</label>
                <textarea className="pj-input" name="description" value={formData.description} onChange={handleChange} placeholder="Provide detailed requirements, technical specifications, and expected deliverables..." />
                {errors.description && <span className="pj-error-text">{errors.description}</span>}
              </div>
            </div>
          </div>
        )}

        {selectedShip && (
          <div className="pj-section">
            <h2 className="pj-section-title"><i className="fa-solid fa-money-bill-wave"></i> Budget & Timeline</h2>
            
            <div className="pj-grid">
              <div className="pj-input-group">
                <label className="pj-label">Budget Type</label>
                <select className="pj-input" name="budgetType" value={formData.budgetType} onChange={handleChange}>
                  <option value="fixed">Fixed Price</option>
                  <option value="hourly">Per Hour</option>
                  <option value="quotation">Request Quotation Only</option>
                </select>
              </div>

              <div className="pj-input-group">
                <label className="pj-label">Currency</label>
                <select className="pj-input" name="currency" value={formData.currency} onChange={handleChange}>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                </select>
              </div>

              <div className="pj-input-group">
                <label className="pj-label">Deadline</label>
                <input 
                  className="pj-input pj-date-input" 
                  type="date" 
                  name="deadline" 
                  value={formData.deadline} 
                  onChange={handleChange} 
                  min={getTomorrowDate()}
                />
                {errors.deadline && <span className="pj-error-text">{errors.deadline}</span>}
              </div>

              {formData.budgetType !== 'quotation' && (
                <>
                  <div className="pj-input-group">
                    <label className="pj-label">Minimum Amount</label>
                    <div className="pj-budget-wrapper">
                      <span className="pj-input pj-currency" style={{ display: 'inline-flex', alignItems: 'center', background: '#111', padding: '0 12px', borderRadius: '4px', color: '#fff' }}>
                        {formData.currency}
                      </span>
                      <input className="pj-input" type="number" name="budgetMin" value={formData.budgetMin} onChange={handleChange} placeholder="0.00" min="0" />
                    </div>
                    {errors.budgetMin && <span className="pj-error-text">{errors.budgetMin}</span>}
                  </div>
                  
                  <div className="pj-input-group">
                    <label className="pj-label">Maximum Amount</label>
                    <div className="pj-budget-wrapper">
                      <span className="pj-input pj-currency" style={{ display: 'inline-flex', alignItems: 'center', background: '#111', padding: '0 12px', borderRadius: '4px', color: '#fff' }}>
                        {formData.currency}
                      </span>
                      <input className="pj-input" type="number" name="budgetMax" value={formData.budgetMax} onChange={handleChange} placeholder="0.00" min="0" />
                    </div>
                    {errors.budgetMax && <span className="pj-error-text">{errors.budgetMax}</span>}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {selectedShip && (
          <div className="pj-actions">
            <button type="button" className="pj-btn pj-btn-secondary" onClick={() => navigate('/owner/dashboard')}>
              Cancel
            </button>
            <button type="submit" className="pj-btn pj-btn-primary" disabled={submitting}>
              {submitting ? <><i className="fa-solid fa-spinner fa-spin"></i> Posting...</> : 'Publish Job'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default PostJob;